import { PREMIUM_BOARD } from "@word-duel/game-engine";
import { BOARD_SIZE, type Board, type BoardTile, type Tile } from "@word-duel/shared";

export interface PendingPlacement {
  tile: Tile;
  row: number;
  col: number;
  blankLetter?: string;
}

interface ScoringCell {
  tile: Tile | BoardTile;
  row: number;
  col: number;
  isNew: boolean;
}

export function boardIndex(row: number, col: number): number {
  return row * BOARD_SIZE + col;
}

export function getBoardTile(board: Board, row: number, col: number): BoardTile | null {
  return board[boardIndex(row, col)] ?? null;
}

export function placementAt(
  placements: PendingPlacement[],
  row: number,
  col: number
): PendingPlacement | undefined {
  return placements.find((placement) => placement.row === row && placement.col === col);
}

export function repositionPendingTile(
  placements: PendingPlacement[],
  tileId: string,
  row: number,
  col: number
): PendingPlacement[] {
  const source = placements.find((placement) => placement.tile.id === tileId);
  if (!source || (source.row === row && source.col === col)) return placements;

  const target = placementAt(placements, row, col);
  return placements.map((placement) => {
    if (placement.tile.id === tileId) return { ...placement, row, col };
    if (target && placement.tile.id === target.tile.id) {
      return { ...placement, row: source.row, col: source.col };
    }
    return placement;
  });
}

function premiumAt(row: number, col: number) {
  return PREMIUM_BOARD[boardIndex(row, col)] ?? null;
}

function isBlankTile(tile: Tile): boolean {
  return tile.letter === "?" || tile.letter === "_" || tile.letter === "BLANK";
}

function tileLetter(tile: Tile, blankLetter?: string): string {
  return isBlankTile(tile) ? (blankLetter ?? tile.assignedLetter ?? "?") : tile.letter;
}

function tileValue(tile: Tile): number {
  return isBlankTile(tile) ? 0 : tile.value;
}

function cellAt(
  board: Board,
  placements: PendingPlacement[],
  row: number,
  col: number
): ScoringCell | null {
  const pending = placementAt(placements, row, col);
  if (pending) return { tile: pending.tile, row, col, isNew: true };
  const committed = getBoardTile(board, row, col);
  return committed ? { tile: committed, row, col, isNew: false } : null;
}

function wordFrom(
  board: Board,
  placements: PendingPlacement[],
  origin: PendingPlacement,
  rowStep: number,
  colStep: number
): ScoringCell[] {
  let row = origin.row;
  let col = origin.col;
  while (cellAt(board, placements, row - rowStep, col - colStep)) {
    row -= rowStep;
    col -= colStep;
  }

  const cells: ScoringCell[] = [];
  while (row >= 0 && row < BOARD_SIZE && col >= 0 && col < BOARD_SIZE) {
    const cell = cellAt(board, placements, row, col);
    if (!cell) break;
    cells.push(cell);
    row += rowStep;
    col += colStep;
  }
  return cells;
}

function scoreWord(cells: ScoringCell[], placements: PendingPlacement[]): number {
  let subtotal = 0;
  let wordMultiplier = 1;

  for (const cell of cells) {
    const pending = cell.isNew ? placementAt(placements, cell.row, cell.col) : undefined;
    let value = tileValue(cell.tile);
    if (pending) {
      const premium = premiumAt(cell.row, cell.col);
      if (premium === "DL") value *= 2;
      if (premium === "TL") value *= 3;
      if (premium === "DW") wordMultiplier *= 2;
      if (premium === "TW") wordMultiplier *= 3;
    }
    subtotal += value;
  }
  return subtotal * wordMultiplier;
}

export function estimatePlacementScore(board: Board, placements: PendingPlacement[]): number {
  if (placements.length === 0) return 0;

  const first = placements[0];
  if (!first) return 0;
  const sameRow = placements.every((placement) => placement.row === first.row);
  const sameCol = placements.every((placement) => placement.col === first.col);
  const words: ScoringCell[][] = [];
  const seen = new Set<string>();

  const addWord = (cells: ScoringCell[]) => {
    if (cells.length < 2) return;
    const key = cells.map((cell) => `${cell.row}:${cell.col}`).join("|");
    if (!seen.has(key)) {
      seen.add(key);
      words.push(cells);
    }
  };

  if (placements.length > 1 && sameRow) addWord(wordFrom(board, placements, first, 0, 1));
  if (placements.length > 1 && sameCol) addWord(wordFrom(board, placements, first, 1, 0));

  for (const placement of placements) {
    if (placements.length === 1 || !sameRow) addWord(wordFrom(board, placements, placement, 0, 1));
    if (placements.length === 1 || !sameCol) addWord(wordFrom(board, placements, placement, 1, 0));
  }

  const score = words.reduce((total, word) => total + scoreWord(word, placements), 0);
  return score + (placements.length === 7 ? 50 : 0);
}

export function placementLetters(placements: PendingPlacement[]): string {
  return placements.map(({ tile, blankLetter }) => tileLetter(tile, blankLetter)).join("");
}

export function shuffleTiles<T>(tiles: readonly T[], random = Math.random): T[] {
  const next = [...tiles];
  for (let index = next.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(random() * (index + 1));
    const current = next[index];
    const other = next[swapIndex];
    if (current !== undefined && other !== undefined) {
      next[index] = other;
      next[swapIndex] = current;
    }
  }
  return next;
}

export function tileDisplayLetter(tile: Tile, blankLetter?: string): string {
  return isBlankTile(tile) ? (blankLetter ?? tile.assignedLetter ?? "") : tile.letter;
}

export function tileDisplayName(tile: Tile, blankLetter?: string): string {
  return tileDisplayLetter(tile, blankLetter) || "Blank wildcard";
}

export function premiumLabel(row: number, col: number): string | null {
  return premiumAt(row, col);
}
